/**
 * 系统权限常量定义
 * 权限代码格式: {模块}:{操作}
 * 模块: dashboard, customer, ticket, user, role, attachment, setting
 * 操作: view, create, update, delete, export, import, assign
 */

export interface Permission {
  code: string;
  name: string;
  description: string;
  /** i18n key for name (e.g. permission.dashboard.view) */
  nameKey: string;
  /** i18n key for description (e.g. permission.dashboard.viewDesc) */
  descriptionKey: string;
  module: string;
}

export interface PermissionModule {
  name: string;
  label: string;
  /** i18n key for module label (e.g. permission.module.dashboard) */
  labelKey: string;
  permissions: Permission[];
}

// 仪表板权限
const DASHBOARD_PERMISSIONS: Permission[] = [
  {
    code: 'dashboard:view',
    name: 'View Dashboard',
    description: 'Allow viewing dashboard and statistics',
    nameKey: 'permission.dashboard.view',
    descriptionKey: 'permission.dashboard.viewDesc',
    module: 'dashboard',
  },
  {
    code: 'dashboard:export',
    name: 'Export Data',
    description: 'Allow exporting dashboard statistics',
    nameKey: 'permission.dashboard.export',
    descriptionKey: 'permission.dashboard.exportDesc',
    module: 'dashboard',
  },
];

// 客户管理权限
const CUSTOMER_PERMISSIONS: Permission[] = [
  {
    code: 'customer:view',
    name: 'View Customers',
    description: 'Allow viewing customer list and details',
    nameKey: 'permission.customer.view',
    descriptionKey: 'permission.customer.viewDesc',
    module: 'customer',
  },
  {
    code: 'customer:create',
    name: 'Create Customer',
    description: 'Allow creating new customers',
    nameKey: 'permission.customer.create',
    descriptionKey: 'permission.customer.createDesc',
    module: 'customer',
  },
  {
    code: 'customer:update',
    name: 'Edit Customer',
    description: 'Allow editing customer information',
    nameKey: 'permission.customer.update',
    descriptionKey: 'permission.customer.updateDesc',
    module: 'customer',
  },
  {
    code: 'customer:delete',
    name: 'Delete Customer',
    description: 'Allow deleting customers',
    nameKey: 'permission.customer.delete',
    descriptionKey: 'permission.customer.deleteDesc',
    module: 'customer',
  },
  {
    code: 'customer:export',
    name: 'Export Customers',
    description: 'Allow exporting customer data',
    nameKey: 'permission.customer.export',
    descriptionKey: 'permission.customer.exportDesc',
    module: 'customer',
  },
  {
    code: 'customer:import',
    name: 'Import Customers',
    description: 'Allow bulk importing customers',
    nameKey: 'permission.customer.import',
    descriptionKey: 'permission.customer.importDesc',
    module: 'customer',
  },
];

// 工单管理权限
const TICKET_PERMISSIONS: Permission[] = [
  {
    code: 'ticket:view',
    name: 'View Tickets',
    description: 'Allow viewing ticket list and details',
    nameKey: 'permission.ticket.view',
    descriptionKey: 'permission.ticket.viewDesc',
    module: 'ticket',
  },
  {
    code: 'ticket:create',
    name: 'Create Ticket',
    description: 'Allow creating new tickets',
    nameKey: 'permission.ticket.create',
    descriptionKey: 'permission.ticket.createDesc',
    module: 'ticket',
  },
  {
    code: 'ticket:update',
    name: 'Edit Ticket',
    description: 'Allow editing ticket information',
    nameKey: 'permission.ticket.update',
    descriptionKey: 'permission.ticket.updateDesc',
    module: 'ticket',
  },
  {
    code: 'ticket:delete',
    name: 'Delete Ticket',
    description: 'Allow deleting tickets',
    nameKey: 'permission.ticket.delete',
    descriptionKey: 'permission.ticket.deleteDesc',
    module: 'ticket',
  },
  {
    code: 'ticket:assign',
    name: 'Assign Ticket',
    description: 'Allow assigning tickets to other users',
    nameKey: 'permission.ticket.assign',
    descriptionKey: 'permission.ticket.assignDesc',
    module: 'ticket',
  },
  {
    code: 'ticket:export',
    name: 'Export Tickets',
    description: 'Allow exporting ticket data',
    nameKey: 'permission.ticket.export',
    descriptionKey: 'permission.ticket.exportDesc',
    module: 'ticket',
  },
];

// 用户管理权限
const USER_PERMISSIONS: Permission[] = [
  {
    code: 'user:view',
    name: 'View Users',
    description: 'Allow viewing user list and details',
    nameKey: 'permission.user.view',
    descriptionKey: 'permission.user.viewDesc',
    module: 'user',
  },
  {
    code: 'user:create',
    name: 'Create User',
    description: 'Allow creating new users',
    nameKey: 'permission.user.create',
    descriptionKey: 'permission.user.createDesc',
    module: 'user',
  },
  {
    code: 'user:update',
    name: 'Edit User',
    description: 'Allow editing user information',
    nameKey: 'permission.user.update',
    descriptionKey: 'permission.user.updateDesc',
    module: 'user',
  },
  {
    code: 'user:delete',
    name: 'Delete User',
    description: 'Allow deleting users',
    nameKey: 'permission.user.delete',
    descriptionKey: 'permission.user.deleteDesc',
    module: 'user',
  },
  {
    code: 'user:activate',
    name: 'Activate User',
    description: 'Allow activating customers or suppliers without email verification',
    nameKey: 'permission.user.activate',
    descriptionKey: 'permission.user.activateDesc',
    module: 'user',
  },
  {
    code: 'user:assign_role',
    name: 'Assign Role',
    description: 'Allow assigning roles to users',
    nameKey: 'permission.user.assignRole',
    descriptionKey: 'permission.user.assignRoleDesc',
    module: 'user',
  },
];

// 角色管理权限
const ROLE_PERMISSIONS: Permission[] = [
  {
    code: 'role:view',
    name: 'View Roles',
    description: 'Allow viewing role list and details',
    nameKey: 'permission.role.view',
    descriptionKey: 'permission.role.viewDesc',
    module: 'role',
  },
  {
    code: 'role:create',
    name: 'Create Role',
    description: 'Allow creating new roles',
    nameKey: 'permission.role.create',
    descriptionKey: 'permission.role.createDesc',
    module: 'role',
  },
  {
    code: 'role:update',
    name: 'Edit Role',
    description: 'Allow editing role information',
    nameKey: 'permission.role.update',
    descriptionKey: 'permission.role.updateDesc',
    module: 'role',
  },
  {
    code: 'role:delete',
    name: 'Delete Role',
    description: 'Allow deleting roles',
    nameKey: 'permission.role.delete',
    descriptionKey: 'permission.role.deleteDesc',
    module: 'role',
  },
  {
    code: 'role:assign_permission',
    name: 'Assign Permissions',
    description: 'Allow assigning permissions to roles',
    nameKey: 'permission.role.assignPermission',
    descriptionKey: 'permission.role.assignPermissionDesc',
    module: 'role',
  },
];

// 附件管理权限
const ATTACHMENT_PERMISSIONS: Permission[] = [
  {
    code: 'attachment:view',
    name: 'View Attachments',
    description: 'Allow viewing attachment list and details',
    nameKey: 'permission.attachment.view',
    descriptionKey: 'permission.attachment.viewDesc',
    module: 'attachment',
  },
  {
    code: 'attachment:upload',
    name: 'Upload Attachment',
    description: 'Allow uploading attachments',
    nameKey: 'permission.attachment.upload',
    descriptionKey: 'permission.attachment.uploadDesc',
    module: 'attachment',
  },
  {
    code: 'attachment:download',
    name: 'Download Attachment',
    description: 'Allow downloading attachments',
    nameKey: 'permission.attachment.download',
    descriptionKey: 'permission.attachment.downloadDesc',
    module: 'attachment',
  },
  {
    code: 'attachment:delete',
    name: 'Delete Attachment',
    description: 'Allow deleting attachments',
    nameKey: 'permission.attachment.delete',
    descriptionKey: 'permission.attachment.deleteDesc',
    module: 'attachment',
  },
];

// 产品管理权限
const PRODUCT_PERMISSIONS: Permission[] = [
  {
    code: 'product:view',
    name: 'View Products',
    description: 'Allow viewing product list and details',
    nameKey: 'permission.product.view',
    descriptionKey: 'permission.product.viewDesc',
    module: 'product',
  },
  {
    code: 'product:create',
    name: 'Create Product',
    description: 'Allow creating new products',
    nameKey: 'permission.product.create',
    descriptionKey: 'permission.product.createDesc',
    module: 'product',
  },
  {
    code: 'product:update',
    name: 'Edit Product',
    description: 'Allow editing product information',
    nameKey: 'permission.product.update',
    descriptionKey: 'permission.product.updateDesc',
    module: 'product',
  },
  {
    code: 'product:delete',
    name: 'Delete Product',
    description: 'Allow deleting products',
    nameKey: 'permission.product.delete',
    descriptionKey: 'permission.product.deleteDesc',
    module: 'product',
  },
  {
    code: 'product:approve',
    name: 'Approve Product',
    description: 'Allow approving products',
    nameKey: 'permission.product.approve',
    descriptionKey: 'permission.product.approveDesc',
    module: 'product',
  },
  {
    code: 'product:export',
    name: 'Export Products',
    description: 'Allow exporting product data',
    nameKey: 'permission.product.export',
    descriptionKey: 'permission.product.exportDesc',
    module: 'product',
  },
  {
    code: 'product:import',
    name: 'Import Products',
    description: 'Allow importing products from CSV',
    nameKey: 'permission.product.import',
    descriptionKey: 'permission.product.importDesc',
    module: 'product',
  },
  {
    code: 'product:import_status',
    name: 'View Product Import Status',
    description: 'Allow viewing CSV import task status',
    nameKey: 'permission.product.importStatus',
    descriptionKey: 'permission.product.importStatusDesc',
    module: 'product',
  },
];

// 系统设置权限
const SETTING_PERMISSIONS: Permission[] = [
  {
    code: 'setting:view',
    name: 'View Settings',
    description: 'Allow viewing system settings',
    nameKey: 'permission.setting.view',
    descriptionKey: 'permission.setting.viewDesc',
    module: 'setting',
  },
  {
    code: 'setting:update',
    name: 'Modify Settings',
    description: 'Allow modifying system settings',
    nameKey: 'permission.setting.update',
    descriptionKey: 'permission.setting.updateDesc',
    module: 'setting',
  },
];

// 所有权限模块
export const PERMISSION_MODULES: PermissionModule[] = [
  {
    name: 'dashboard',
    label: 'Dashboard',
    labelKey: 'permission.module.dashboard',
    permissions: DASHBOARD_PERMISSIONS,
  },
  {
    name: 'customer',
    label: 'Customer Management',
    labelKey: 'permission.module.customer',
    permissions: CUSTOMER_PERMISSIONS,
  },
  {
    name: 'ticket',
    label: 'Ticket Management',
    labelKey: 'permission.module.ticket',
    permissions: TICKET_PERMISSIONS,
  },
  {
    name: 'user',
    label: 'User Management',
    labelKey: 'permission.module.user',
    permissions: USER_PERMISSIONS,
  },
  {
    name: 'role',
    label: 'Role Management',
    labelKey: 'permission.module.role',
    permissions: ROLE_PERMISSIONS,
  },
  {
    name: 'attachment',
    label: 'Attachment Management',
    labelKey: 'permission.module.attachment',
    permissions: ATTACHMENT_PERMISSIONS,
  },
  {
    name: 'setting',
    label: 'System Settings',
    labelKey: 'permission.module.setting',
    permissions: SETTING_PERMISSIONS,
  },
];

// 所有权限列表（扁平化）
export const ALL_PERMISSIONS: Permission[] = PERMISSION_MODULES.flatMap(
  (module) => module.permissions
);

// 权限代码映射（便于快速查找）
export const PERMISSION_MAP: Record<string, Permission> = ALL_PERMISSIONS.reduce(
  (map, permission) => {
    map[permission.code] = permission;
    return map;
  },
  {} as Record<string, Permission>
);

// 根据模块获取权限
export function getPermissionsByModule(moduleName: string): Permission[] {
  const module = PERMISSION_MODULES.find((m) => m.name === moduleName);
  return module ? module.permissions : [];
}

// 检查权限代码是否有效
export function isValidPermission(code: string): boolean {
  return code in PERMISSION_MAP;
}

// 获取权限名称
export function getPermissionName(code: string): string {
  return PERMISSION_MAP[code]?.name || code;
}

// 获取权限描述
export function getPermissionDescription(code: string): string {
  return PERMISSION_MAP[code]?.description || '';
}
